import { useCallback, useEffect, useLayoutEffect, useState, type ReactElement } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Toolbar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Routes, Route, Navigate, useLocation } from 'react-router';
import theme from './theme/theme';
import i18n from './i18n';
import LanguageSelector from './components/LanguageSelector';
import NavDrawer from './components/NavDrawer';
import Application from './pages/Application';
import AvondaleDocument from './pages/AvondaleDocument';
import AvondaleHandbook from './pages/AvondaleHandbook';
import Documents from './pages/Documents';
import HIPAATraining from './pages/HIPAATraining';
import AIAwarenessTraining from './pages/AIAwarenessTraining';
import Login from './pages/Login';
import Onboarding from './pages/Onboarding';
import NursingAssessment from './pages/NursingAssessment';
import QuickEntry from './pages/QuickEntry/QuickEntry';
import QuickEntryLanding from './pages/QuickEntry/QuickEntryLanding';
import Referral from './pages/Referral';
import Start from './pages/Start';
import TemplateDetail from './pages/TemplateDetail';
import Templates from './pages/Templates';
import { LicenseInfo } from '@mui/x-license';
import type { CaregiverFormRoute } from './utils/auth';
import {
  AUTH_CHANGE_EVENT,
  AuthUser,
  canAccessCaregiverFormRoute,
  getCaregiverFormRoute,
  getDefaultAuthenticatedRoute,
  getStoredUser,
  hasRole,
  NURSE_ADMIN_SESSION_KEY,
  NURSING_SELECTED_PATIENT_KEY,
  REFERRAL_SESSION_KEY,
  refreshStoredUser,
  storeAuthSession,
} from './utils/auth';
import { initializeUserPortalLanguage, notifyPortalLanguageChange } from './utils/portalLanguage';
import { clearStoredQuickEntryUser, setStoredQuickEntryUser } from './utils/quickEntryUser';
import { MEDICAL_RECRUITMENT_NYC_ACCOUNT_ID } from './utils/cmReferralRoute';
import { setTelemetryUser } from './utils/telemetry';

LicenseInfo.setLicenseKey(
  'ae76c599367b237c357650e27c548556Tz0xMjM3NTQsRT0xNzk4ODQ3OTk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y'
);

function RouteLoading() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
      <CircularProgress />
    </Box>
  );
}

function ProtectedRoute({ route, children }: { route: CaregiverFormRoute; children: ReactElement }) {
  const [user, setUser] = useState(() => getStoredUser());
  const [checkingAuth, setCheckingAuth] = useState(() => Boolean(getStoredUser()));

  useEffect(() => {
    const storedUser = getStoredUser();
    setUser(storedUser);

    if (!storedUser) {
      setCheckingAuth(false);
      return undefined;
    }

    let cancelled = false;
    setCheckingAuth(true);
    refreshStoredUser()
      .then((refreshedUser) => {
        if (!cancelled) setUser(refreshedUser);
      })
      .catch(() => {
        if (!cancelled) setUser(getStoredUser());
      })
      .finally(() => {
        if (!cancelled) setCheckingAuth(false);
      });

    return () => {
      cancelled = true;
    };
  }, [route]);

  if (checkingAuth) return <RouteLoading />;
  if (!user) return <Navigate to="/start" replace />;

  if (!canAccessCaregiverFormRoute(user, route)) return <Navigate to={getDefaultAuthenticatedRoute(user)} replace />;

  return children;
}

function AdminRoute({ children }: { children: ReactElement }) {
  const user = getStoredUser();
  if (!user) return <Navigate to="/start" replace />;
  if (!hasRole(user, 'admin')) return <Navigate to={getDefaultAuthenticatedRoute(user)} replace />;

  return children;
}

function RoleRoute({
  roles,
  redirectTo = '/start',
  children,
}: {
  roles: string[];
  redirectTo?: string;
  children: ReactElement;
}) {
  const user = getStoredUser();
  if (!user) return <Navigate to="/start" replace />;
  if (!roles.some((role) => hasRole(user, role))) return <Navigate to={redirectTo} replace />;

  return children;
}

function DefaultRouteRedirect() {
  const user = getStoredUser();
  return <Navigate to={user ? getDefaultAuthenticatedRoute(user) : '/start'} replace />;
}

const LINK_BRAND = {
  title: 'Link Portal',
  logoSrc: '/images/logo/evo-logo-link.png',
  logoAlt: 'Link Logo',
  favicon: '/images/fav.png',
  touchIcon: '/images/logo/evo-logo-link.png',
};
const AVONDALE_BRAND = {
  title: 'Avondale Portal',
  logoSrc: '/images/logo/avondale-logo.png',
  logoAlt: 'Avondale Logo',
  favicon: '/images/favicon-avondale.png',
  touchIcon: '/images/logo/avondale-logo.png',
};

interface NurseAdminSessionBackup {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
  nurseName: string;
}

interface ReferralSessionBackup {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

function readNurseAdminBackup(): NurseAdminSessionBackup | null {
  try {
    const raw = localStorage.getItem(NURSE_ADMIN_SESSION_KEY);
    return raw ? (JSON.parse(raw) as NurseAdminSessionBackup) : null;
  } catch {
    return null;
  }
}

function readReferralBackup(): ReferralSessionBackup | null {
  try {
    const raw = localStorage.getItem(REFERRAL_SESSION_KEY);
    return raw ? (JSON.parse(raw) as ReferralSessionBackup) : null;
  } catch {
    return null;
  }
}

function setLinkHref(rel: string, href: string): void {
  let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
}

function setMetaProperty(property: string, content: string): void {
  let meta = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.content = content;
}

function AppContent() {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => getStoredUser());
  const [nurseAdminBackup, setNurseAdminBackup] = useState<NurseAdminSessionBackup | null>(() => readNurseAdminBackup());
  const [referralBackup, setReferralBackup] = useState<ReferralSessionBackup | null>(() => readReferralBackup());
  const isAvondaleRoute = location.pathname.startsWith('/avondale-');
  const activeBrand = isAvondaleRoute ? AVONDALE_BRAND : LINK_BRAND;

  const syncCurrentUser = useCallback(() => {
    setCurrentUser(getStoredUser());
    setNurseAdminBackup(readNurseAdminBackup());
    setReferralBackup(readReferralBackup());
  }, []);

  useEffect(() => {
    syncCurrentUser();
  }, [location.key, syncCurrentUser]);

  useLayoutEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (location.pathname === '/start') {
      if (searchParams.has('user') || searchParams.has('USER')) {
        setStoredQuickEntryUser(searchParams.get('user') || searchParams.get('USER'));
      } else {
        clearStoredQuickEntryUser();
      }
    } else if (location.pathname === '/quick-entry' && (searchParams.has('user') || searchParams.has('USER'))) {
      setStoredQuickEntryUser(searchParams.get('user') || searchParams.get('USER'));
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    window.addEventListener(AUTH_CHANGE_EVENT, syncCurrentUser);
    window.addEventListener('storage', syncCurrentUser);

    return () => {
      window.removeEventListener(AUTH_CHANGE_EVENT, syncCurrentUser);
      window.removeEventListener('storage', syncCurrentUser);
    };
  }, [syncCurrentUser]);

  useEffect(() => {
    setTelemetryUser(currentUser?.id);
  }, [currentUser?.id]);

  useEffect(() => {
    if (!currentUser?.nexusCaregiver) return;

    const locale = initializeUserPortalLanguage(
      currentUser.id,
      currentUser.nexusCaregiver.primaryLanguage
    );
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale);
      notifyPortalLanguageChange();
    }
  }, [currentUser?.id, currentUser?.nexusCaregiver?.primaryLanguage]);

  useEffect(() => {
    document.title = activeBrand.title;
    setLinkHref('icon', activeBrand.favicon);
    setLinkHref('apple-touch-icon', activeBrand.touchIcon);
    setMetaProperty('og:image', activeBrand.touchIcon);
  }, [activeBrand]);

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh">
      {nurseAdminBackup && (
        <Alert
          severity="info"
          sx={{
            position: 'relative',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            borderRadius: 0,
            margin: 0,
            padding: '8px 16px',
            minHeight: 'auto',
            bgcolor: '#e8f4fd',
            '& .MuiAlert-message': {
              width: '100%',
              padding: 0,
            },
            '& .MuiAlert-icon': {
              padding: 0,
              marginRight: '8px',
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              <strong>Acting as:</strong> {nurseAdminBackup.nurseName} &bull;
              <span style={{ marginLeft: '8px' }}>
                You are logged in as another nurse user
              </span>
            </Typography>
            <Button
              onClick={() => {
                storeAuthSession(nurseAdminBackup.accessToken, nurseAdminBackup.refreshToken, nurseAdminBackup.user);
                localStorage.removeItem(NURSE_ADMIN_SESSION_KEY);
                localStorage.removeItem(NURSING_SELECTED_PATIENT_KEY);
                setNurseAdminBackup(null);
                setCurrentUser(nurseAdminBackup.user);
                window.location.assign('/nursing-assessment');
              }}
              size="small"
              variant="outlined"
              sx={{
                ml: 2,
                fontSize: '0.75rem',
                padding: '4px 8px',
                bgcolor: 'white',
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.8)' },
              }}
            >
              Return to Admin Account
            </Button>
          </Box>
        </Alert>
      )}
      {referralBackup && (
        <Alert
          severity="info"
          sx={{
            position: 'relative',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            borderRadius: 0,
            margin: 0,
            padding: '8px 16px',
            minHeight: 'auto',
            bgcolor: '#e8f4fd',
            '& .MuiAlert-message': {
              width: '100%',
              padding: 0,
            },
            '& .MuiAlert-icon': {
              padding: 0,
              marginRight: '8px',
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              <strong>Acting as:</strong> Caregiver &bull;
              <span style={{ marginLeft: '8px' }}>You are logged in from the referral portal</span>
            </Typography>
            <Button
              onClick={() => {
                storeAuthSession(referralBackup.accessToken, referralBackup.refreshToken, referralBackup.user);
                localStorage.removeItem(REFERRAL_SESSION_KEY);
                setReferralBackup(null);
                setCurrentUser(referralBackup.user);
                window.location.assign('/referral');
              }}
              size="small"
              variant="outlined"
              sx={{
                ml: 2,
                fontSize: '0.75rem',
                padding: '4px 8px',
                bgcolor: 'white',
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.8)' },
              }}
            >
              Return to Referral Portal
            </Button>
          </Box>
        </Alert>
      )}
      <AppBar position="static" sx={{ backgroundColor: '#FFFFFF' }}>
        <Toolbar sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between' }}>
          <Box
            component="img"
            src={activeBrand.logoSrc}
            alt={activeBrand.logoAlt}
            sx={{
              maxHeight: isAvondaleRoute ? 44 : 40,
              maxWidth: isAvondaleRoute ? { xs: 190, sm: 230 } : undefined,
              transition: 'transform 0.3s ease',
              '&:hover': { transform: 'scale(1.05)' },
            }}
          />
          <Box
            id="nursing-top-nav-tools"
            sx={{
              position: 'absolute',
              left: { xs: '56%', sm: '50%' },
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 1.5 },
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {currentUser?.nexusCaregiver && <LanguageSelector currentUser={currentUser} />}
            {currentUser && (
              <IconButton
                edge="end"
                aria-label="menu"
                onClick={() => setDrawerOpen(true)}
                sx={{ ml: 2, color: '#330662' }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
        <Box id="nursing-pinned-info" />
      </AppBar>

      <NavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} currentUser={currentUser} />

      <Routes>
        <Route path="/" element={<Navigate to="/start" replace />} />
        <Route path="/start" element={<Start />} />
        <Route
          path="/mrnyc"
          element={<Start cmReferralAccountId={MEDICAL_RECRUITMENT_NYC_ACCOUNT_ID} />}
        />
        <Route path="/login" element={<Login />} />
        <Route
          path="/application"
          element={(
            <ProtectedRoute route="/application">
              <Application />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/onboarding"
          element={(
            <ProtectedRoute route="/onboarding">
              <Onboarding />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/nursing-assessment"
          element={(
            <ProtectedRoute route="/nursing-assessment">
              <NursingAssessment />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/templates"
          element={(
            <AdminRoute>
              <Templates />
            </AdminRoute>
          )}
        />
        <Route
          path="/templates/:templateId"
          element={(
            <AdminRoute>
              <TemplateDetail />
            </AdminRoute>
          )}
        />
        <Route
          path="/documents"
          element={(
            <AdminRoute>
              <Documents />
            </AdminRoute>
          )}
        />
        <Route
          path="/qe-landing"
          element={(
            <RoleRoute roles={['quick-entry', 'admin']} redirectTo="/start">
              <QuickEntryLanding />
            </RoleRoute>
          )}
        />
        <Route
          path="/referral"
          element={(
            <RoleRoute roles={['referral', 'admin']} redirectTo="/start">
              <Referral />
            </RoleRoute>
          )}
        />
        <Route path="/quick-entry" element={<QuickEntry />} />
        <Route path="/hipaa-training" element={<HIPAATraining />} />
        <Route path="/ai-awareness-training" element={<AIAwarenessTraining />} />
        <Route path="/avondale-flu" element={<AvondaleDocument kind="flu" />} />
        <Route path="/avondale-caregiver" element={<AvondaleDocument kind="caregiver" />} />
        <Route path="/avondale-wage-notice" element={<AvondaleDocument kind="wage" />} />
        <Route path="/avondale-handbook" element={<AvondaleHandbook />} />
        <Route path="*" element={<DefaultRouteRedirect />} />
      </Routes>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
